import type {
  EhrEncounterSnapshot,
  EhrNoteSnapshot,
} from "@astrana/ui/scribe";

import { normalizeSections } from "@ext/elation/elationWriteBack";

import type {
  ElationClinical,
  ElationPatient,
  ElationNote,
} from "~/bridge/types";

/**
 * Elation → EHR-neutral snapshot. The shell owns this; `@astrana/ui/scribe` must
 * never learn what Elation is (spec §2.2).
 *
 * Deliberately free of STRUCTURED demographics: no name, DOB, sex, age or phone
 * field is copied. It is NOT a claim that the payload is name-free — every
 * `currentText`, every synthesized legacy body and every prior-note body is
 * clinical free text, which routinely opens "Jane Doe is a 54yo F…". Anything
 * downstream that needs a name-free payload must redact, not rely on this.
 *
 * MINIMUM NECESSARY IS ENFORCED HERE, not downstream: the server-side
 * `is_prior_notes_enabled` gate runs after the PHI has already crossed the wire,
 * so this adapter is the last place that can decide not to send it.
 */

const FALLBACK_SOURCE_SYSTEM = "EMR_ELATION";

/**
 * Size ceiling at the boundary. Before this, every signed note the bridge
 * scraped was copied at full body text and every list verbatim: a chronic-care
 * chart with 40-50 prior notes is ~25k tokens of prior notes alone, which is a
 * hard context-length rejection against a 16k deployment (and unbounded PHI
 * egress either way). The backend enforces its own ceiling too — this one exists
 * so the PHI never leaves the browser, not merely so the prompt fits.
 *
 * Five prior notes matches the design intent; a note generator gains very little
 * from the sixth-most-recent visit while carrying the PHI of all of them.
 */
export const MAX_PRIOR_NOTES = 5;

/** Per-section text cap. Generous for a real note, a ceiling for a copy-forward. */
export const MAX_SECTION_CHARS = 4_000;

/** Per-list cap (problems, medications, allergies, care gaps, …). */
export const MAX_LIST_ITEMS = 100;

const TRUNCATED_MARKER = "…[truncated]";

function truncate(text: string, max: number): string {
  return text.length <= max
    ? text
    : `${text.slice(0, max).trimEnd()}${TRUNCATED_MARKER}`;
}

/**
 * Legacy SOAP fields, used only when the live section list is unavailable.
 *
 * These `key`s are DISPLAY keys, not EHR section keys — Elation's real legacy
 * keys are `assessplan`, `hpi`, `ros`, `pe`, `followup`, `instr` (see
 * elationWriteBack.ts), and it has no `subjective`, `objective`, `assessment`
 * or `plan` section at all. Everything synthesized here is therefore READ
 * CONTEXT ONLY and is emitted with `writable: false`; see the comment on
 * SYNTHESIZED_WRITABLE below.
 */
const LEGACY_SECTION_FIELDS: {
  key: string;
  label: string;
  field: keyof Pick<
    ElationNote,
    "subjective" | "objective" | "assessment" | "plan" | "ros" | "pe" | "followUp"
  >;
}[] = [
  { key: "subjective", label: "Subjective", field: "subjective" },
  { key: "objective", label: "Objective", field: "objective" },
  { key: "assessment", label: "Assessment", field: "assessment" },
  { key: "plan", label: "Plan", field: "plan" },
  { key: "ros", label: "Review of Systems", field: "ros" },
  { key: "pe", label: "Physical Exam", field: "pe" },
  { key: "followup", label: "Follow-up", field: "followUp" },
];

/**
 * Synthesized sections are NEVER writable.
 *
 * `EhrSectionSnapshot.key` is "the exact EHR-side section key — what a write
 * targets", and `elationWriteBack.stageInPage` resolves it directly against the
 * open note's live `_inputSections` map, refusing with `SECTION_UNRESOLVED` on a
 * miss. A synthesized key is not in that map, so a write aimed at one can only
 * fail — and would fail AFTER the clinician clicked "Write to Assessment" on a
 * section the app showed them and Elation says does not exist. (A future
 * transport less strict than stageInPage would be worse still: the write would
 * silently target nothing while the UI reported success.)
 *
 * A write target must only ever come from the LIVE section list.
 */
const SYNTHESIZED_WRITABLE = false;

/**
 * The declared TS types here are assertions about UNTRUSTED JSON: everything on
 * `ElationClinical` crosses the extension `postMessage` boundary, so a scrape
 * miss can hand us `["Metformin", null]` or `{"BP": null}` where the type says
 * `string`. Without the `typeof` guard, `.trim()` throws a TypeError out of a
 * pure adapter with no try/catch at any call site — i.e. a white-screened
 * clinical surface in a repo with no CI gate that deploys `main` to prod.
 */
function clean(items?: string[]): string[] {
  return (items ?? [])
    .filter((i): i is string => typeof i === "string")
    .map((i) => i.trim())
    .filter(Boolean)
    .slice(0, MAX_LIST_ITEMS);
}

function toNoteSnapshot(note: ElationNote): EhrNoteSnapshot {
  // Prefer the REAL sections read from the live page: their keys are what a
  // write-back targets. The legacy field synthesis is a read-only fallback for
  // signed notes and non-legacy engines, where no section list is provided.
  //
  // `normalizeSections` rather than a hand-rolled map, because it is the
  // decision point the sibling consumer (`ElationEncounterPanel`) already uses:
  // it drops entries without a string `key`, coerces `label`/`currentText`, and
  // ANDs the MAIN-world read's best-effort `writable` flag with the pure
  // structured-key blocklist — "a structured key is never writable even if the
  // read misjudged it". Copying `s.writable` verbatim gave the path that feeds
  // an AI proposing writes WEAKER guarantees than the path a human clicks
  // through: a mis-flagged `orders`/`meds`/`vitals` section became a write
  // target for clinical prose.
  const live = normalizeSections(note.sections);
  const sections =
    live.length > 0
      ? live.map((s) => ({
          key: s.key,
          label: s.label,
          currentText: truncate(s.currentText, MAX_SECTION_CHARS),
          // `writable` is optional on the bridge type and REQUIRED (fail-closed)
          // on the library's; `=== true` is the coercion, not a redundancy.
          writable: s.writable === true,
        }))
      : LEGACY_SECTION_FIELDS.flatMap(({ key, label, field }) => {
          const raw = note[field];
          const currentText = typeof raw === "string" ? raw.trim() : "";
          return currentText
            ? [
                {
                  key,
                  label,
                  currentText: truncate(currentText, MAX_SECTION_CHARS),
                  writable: SYNTHESIZED_WRITABLE,
                },
              ]
            : [];
        });

  return {
    status: note.status,
    visitDate: note.visitDate,
    visitType: note.visitType,
    provider: note.provider,
    sections,
  };
}

/**
 * What the library's `detectVisitNoteType` matches on: it concatenates the open
 * note's `visitType` with the snapshot's and pattern-matches the result. Before
 * the template read, both were the same string — the visit-type <select>'s text
 * ("Office Visit") — which says nothing about the note TYPE, so detection fell
 * through to its default and every encounter looked like a Simple Note.
 *
 * Appending the template display name ("SOAP Note", "MEAT Note", …) gives that
 * matcher the signal it was missing. Appended, not substituted: the visit type
 * is real information the backend prompt already relies on, and a template whose
 * name repeats the visit type must not be printed twice.
 */
function visitTypeWithTemplate(note: ElationNote | null | undefined): string | undefined {
  const visitType = note?.visitType?.trim() ?? "";
  const template = note?.templateName?.trim() ?? "";
  if (!template || template.toLowerCase() === visitType.toLowerCase()) {
    return visitType || undefined;
  }
  return visitType ? `${visitType} · ${template}` : template;
}

/**
 * Most recent first, so "keep 5" keeps the 5 that matter. `signedNotes` arrives
 * in whatever order the Elation read produced; sorting by `visitDate` descending
 * (undated last, original relative order preserved) makes the selection
 * deterministic rather than dependent on scrape order.
 */
function mostRecentFirst(notes: ElationNote[]): ElationNote[] {
  return notes
    .map((note, index) => {
      const parsed = note.visitDate ? Date.parse(note.visitDate) : NaN;
      return { note, index, time: Number.isNaN(parsed) ? null : parsed };
    })
    .sort((a, b) => {
      if (a.time === null && b.time === null) return a.index - b.index;
      if (a.time === null) return 1;
      if (b.time === null) return -1;
      if (a.time !== b.time) return b.time - a.time;
      return a.index - b.index;
    })
    .map((e) => e.note);
}

export function toEhrEncounterSnapshot(
  clinical: ElationClinical | null | undefined,
  patient: ElationPatient | null | undefined,
): EhrEncounterSnapshot | null {
  if (!clinical) return null;

  const openNote = clinical.openNote
    ? toNoteSnapshot(clinical.openNote)
    : undefined;

  return {
    sourceSystem: patient?.sourceSystem ?? FALLBACK_SOURCE_SYSTEM,
    ehrPatientId: patient?.ehrPatientId ?? undefined,
    // CHART IDENTITY, half two. The library carries this back on every write so
    // the host can fail closed on a mismatch; `ehrPatientId` alone catches a
    // PATIENT switch but not a same-patient ENCOUNTER switch — the clinician
    // signing the open note and starting a new one inside an async round trip,
    // which would land text drafted from encounter A in encounter B.
    // `ElationNote.noteId` is the open note's own id and exists for exactly
    // this purpose; it is absent on signed notes, so this is undefined when there
    // is no open note — which is also when there is nothing to write into.
    encounterKey: clinical.openNote?.noteId ?? undefined,
    visitType: visitTypeWithTemplate(clinical.openNote),
    // Inputs to the note's one-liner (`[Age][Gender] with a history of …`),
    // which is ASSEMBLED from chart data rather than generated. Forwarded
    // exactly as the chart reported them: "67 y" is parsed downstream, and a sex
    // value with no conventional single-letter form is spelled out rather than
    // guessed at. Absent on a chart that reported neither, which drops the
    // clause instead of rendering a placeholder.
    age: patient?.age ?? undefined,
    sex: patient?.sex ?? undefined,
    visitDate: clinical.openNote?.visitDate,
    provider: clinical.openNote?.provider ?? patient?.primaryPhysician ?? undefined,
    openNote,
    // DELIBERATELY `signedNotes`, NOT `chartNotes`.
    //
    // `chartNotes` now also carries unsigned drafts and on-hold notes (that is
    // the point of it — the Notes tab must list them). `priorNotes` is not a UI
    // list: it is PHI that leaves the browser for the note generator. Widening
    // it here would silently start sending half-written, unreviewed drafts to a
    // backend prompt as if they were finished chart history — a change to what
    // is disclosed and to what the model is grounded on, neither of which was
    // asked for. If drafts should ever ground generation, that is its own
    // decision with its own review, made here explicitly.
    priorNotes: mostRecentFirst(clinical.signedNotes ?? [])
      .slice(0, MAX_PRIOR_NOTES)
      .map(toNoteSnapshot),
    problemList: clean(clinical.problemList),
    medications: clean(clinical.medications),
    allergies: clean(clinical.allergies),
    vitals: Object.entries(clinical.openNote?.vitals ?? {})
      .filter(([, value]) => typeof value === "string" && value.trim())
      .map(([name, value]) => ({ name, value: value.trim() })),
    careGaps: clean(clinical.careGaps),
    immunizations: clean(clinical.immunizations),
    pastProblems: clean(clinical.pastProblems),
  };
}
